import React, { useState, useEffect } from 'react';
import API from '../utils/api';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Modal, ConfirmDialog, PageLoader, EmptyState, Badge, ActionBtn, SectionHeader, FormField, StatCard } from '../components/common/UI';
import { toShamsi, formatCurrency } from '../utils/dateHelpers';
import { useAuth } from '../context/AuthContext';
import { FaMoneyBillWave, FaCreditCard, FaHandshake, FaProjectDiagram, FaChalkboardTeacher } from 'react-icons/fa';
import { MdReceipt, MdHistory } from 'react-icons/md';
import ShamsiDatePicker from '../components/common/ShamsiDatePicker';

export default function Debts() {
  const { isManager } = useAuth();
  const [loading, setLoading] = useState(true);
  const [debts, setDebts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterType, setFilterType] = useState('');
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [viewHistory, setViewHistory] = useState(null);

  const fetchDebts = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 10 };
      if (filterType) params.type = filterType;
      const res = await API.get('/debts', { params });
      setDebts(res.data.data);
      setTotalPages(res.data.pages);
    } catch (err) {
      toast.error('خطا در بارگذاری');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDebts();
  }, [page, filterType]);

  const handlePayment = async () => {
    if (!paymentAmount || paymentAmount <= 0) return toast.error('مبلغ را وارد کنید');
    try {
      const payload = {
        amount: Number(paymentAmount),
        type: selectedDebt.type
      };
      if (selectedDebt.type === 'class') {
        payload.classId = selectedDebt.classId;
        payload.studentId = selectedDebt.studentId;
      }
      await API.post(`/debts/${selectedDebt._id}/pay`, payload);
      toast.success('پرداخت ثبت شد');
      setShowPaymentModal(false);
      setPaymentAmount('');
      fetchDebts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطا');
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'project': return <FaProjectDiagram className="text-blue-600" />;
      case 'sponsorship': return <FaHandshake className="text-purple-600" />;
      case 'class': return <FaChalkboardTeacher className="text-yellow-600" />;
      default: return <FaCreditCard className="text-gray-500" />;
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'project': return 'پروژه';
      case 'sponsorship': return 'اسپانسرشیپ';
      case 'class': return 'کلاس';
      default: return type;
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-5 fade-in">
      <SectionHeader 
        title="لیست بدهکاران" 
        subtitle={`${debts.length} مورد با باقی‌مانده`}
        actions={
          <Link to="/finance" className="px-4 py-2 bg-gray-100 rounded-xl text-sm hover:bg-gray-200">
            ← بازگشت به مالی
          </Link>
        }
      />

      {/* فیلتر */}
      <div className="glass-card p-4">
        <select 
          className="form-input w-full sm:w-48" 
          value={filterType} 
          onChange={e => { setFilterType(e.target.value); setPage(1); }}
        >
          <option value="">همه</option>
          <option value="project">پروژه‌ها</option>
          <option value="sponsorship">اسپانسرشیپ</option>
          <option value="class">کلاس‌ها</option>
        </select>
      </div>

      {debts.length === 0 ? (
        <EmptyState icon="💰" title="بدهکاری وجود ندارد" subtitle="همه مشتریان تسویه حساب کرده‌اند" />
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {debts.map(debt => {
              const remaining = debt.totalAmount - debt.paidAmount;
              return (
                <div key={debt._id} className="glass-card p-4 border border-red-200">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                        {getTypeIcon(debt.type)}
                      </div>
                      <div>
                        <h3 className="text-gray-800 font-semibold">{debt.name}</h3>
                        <p className="text-xs text-gray-500">{getTypeLabel(debt.type)} • {debt.clientName}</p>
                      </div>
                    </div>
                    <span className="badge bg-red-100 text-red-600">بدهکار</span>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mt-4 pt-3 border-t border-gray-200">
                    <div>
                      <div className="text-xs text-gray-600">مبلغ کل</div>
                      <div className="text-green-600 font-bold">{formatCurrency(debt.totalAmount)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">پرداخت شده</div>
                      <div className="text-blue-600">{formatCurrency(debt.paidAmount)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">باقی‌مانده</div>
                      <div className="text-red-600 font-bold">{formatCurrency(remaining)}</div>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <button 
                      onClick={() => { setSelectedDebt(debt); setShowPaymentModal(true); }}
                      className="flex-1 py-2 bg-green-100 text-green-600 rounded-lg text-sm hover:bg-green-200 transition-colors"
                    >
                       پرداخت
                    </button>
                    <button 
                      onClick={() => setViewHistory(debt)}
                      className="flex-1 py-2 bg-blue-100 text-blue-600 rounded-lg text-sm hover:bg-blue-200 transition-colors"
                    >
                       تاریخچه پرداخت
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} className="px-3 py-1 rounded-lg bg-gray-100 disabled:opacity-50">قبلی</button>
              <span className="px-3 py-1 text-gray-800">صفحه {page} از {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages} className="px-3 py-1 rounded-lg bg-gray-100 disabled:opacity-50">بعدی</button>
            </div>
          )}
        </>
      )}

      {/* مودال پرداخت */}
      {showPaymentModal && selectedDebt && (
        <Modal isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} title="پرداخت" size="sm">
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-gray-800">{selectedDebt.name}</div>
              <div className="text-red-600 text-xl font-bold mt-1">
                {formatCurrency(selectedDebt.totalAmount - selectedDebt.paidAmount)}
              </div>
              <div className="text-gray-500 text-sm">باقی‌مانده</div>
            </div>
            <input type="number" className="form-input" placeholder="مبلغ پرداختی" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowPaymentModal(false)} className="px-4 py-2 bg-gray-100 rounded-lg">انصراف</button>
              <button onClick={handlePayment} className="px-4 py-2 bg-green-600 rounded-lg">پرداخت</button>
            </div>
          </div>
        </Modal>
      )}

      {/* مودال تاریخچه پرداخت */}
      {viewHistory && (
        <Modal isOpen={!!viewHistory} onClose={() => setViewHistory(null)} title={`تاریخچه پرداخت‌ها - ${viewHistory.name}`} size="lg">
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-600">مبلغ کل:</span> <span className="text-green-600">{formatCurrency(viewHistory.totalAmount)}</span></div>
                <div><span className="text-gray-600">پرداخت شده:</span> <span className="text-blue-600">{formatCurrency(viewHistory.paidAmount)}</span></div>
                <div><span className="text-gray-600">باقی‌مانده:</span> <span className="text-red-600">{formatCurrency(viewHistory.totalAmount - viewHistory.paidAmount)}</span></div>
              </div>
            </div>

            {viewHistory.payments?.length > 0 ? (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {viewHistory.payments.map((p, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-3 flex justify-between items-center">
                    <div>
                      <div className="text-gray-800">{formatCurrency(p.amount)}</div>
                      <div className="text-xs text-gray-600">{toShamsi(p.date)}</div>
                    </div>
                    <span className="badge bg-green-100 text-green-700">پرداخت شده</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-600">هیچ پرداختی ثبت نشده</div>
            )}

            <div className="flex justify-end">
              <button onClick={() => setViewHistory(null)} className="px-4 py-2 bg-blue-600 rounded-lg">بستن</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}